import React from 'react';
import { Box, Typography, List, ListItemButton, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EmptyState from '../../../common/EmptyState';

/**
 * RelationTypeSelectStep - Reusable relation type selection step for wizard modals
 *
 * @param {Object} props
 * @param {Array} props.relationTypes - Array of relation types to choose from
 * @param {Object} props.selectedRelationType - Currently selected relation type
 * @param {Function} props.onSelectRelationType - Callback when a relation type is selected
 * @param {React.ReactNode} props.children - Optional content to render above the relation type list (e.g., selected item summary)
 */
const RelationTypeSelectStep = ({
  relationTypes = [],
  selectedRelationType,
  onSelectRelationType,
  children,
}) => {
  return (
    <Box>
      {children}

      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Select Relation Type
      </Typography>

      {relationTypes.length === 0 ? (
        <EmptyState
          title="No relation types"
          description="No relation types are configured"
          textSize="h8"
        />
      ) : (
        <List sx={{ p: 0 }}>
          {relationTypes.map((relationType) => {
            const isSelected = selectedRelationType?.id === relationType.id;
            return (
              <ListItemButton
                key={relationType.id}
                onClick={() => onSelectRelationType(relationType)}
                selected={isSelected}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  border: isSelected ? '2px solid #2563EB' : '1px solid #E2E8F0',
                  backgroundColor: isSelected ? 'rgba(37, 99, 235, 0.05)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isSelected ? 'rgba(37, 99, 235, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(37, 99, 235, 0.05)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                  <Chip
                    label={relationType.name}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '12px',
                      backgroundColor: relationType.color || '#6B7280',
                      color: '#fff',
                      fontWeight: 500,
                    }}
                  />
                  {relationType.description && (
                    <Typography variant="body2" color="text.secondary">
                      {relationType.description}
                    </Typography>
                  )}
                </Box>
                {isSelected && (
                  <CheckIcon sx={{ color: '#2563EB', ml: 1 }} />
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default RelationTypeSelectStep;
